/**
 * easter.js
 * ---------------------------------------------------------------------------
 * Schwesterdatei zu halloween.js und xmas.js: identische Logik, eigener
 * Namensraum (ea-).
 *
 * Erzeugt die Schmetterlinge und Blütenblätter im Hintergrund und lässt sie
 * umherziehen. Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius
 * Pixel, stieben sie auseinander und beruhigen sich danach wieder auf ihr
 * Reisetempo. Der Pollenflug gehört nicht dazu, der läuft rein über CSS.
 *
 * Jede Kreatur bekommt beim Erzeugen eine eigene Farbe aus der jeweiligen
 * Palette, damit sich nichts sichtbar wiederholt.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Sonne und Wiese.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initEaster } from "./easter.js";
 *   initEaster();
 *
 * Aktiviert wird jedes Element mit der Klasse .ea-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-easter.
 */
export function easter() {
    "use strict";

    var CONFIG = {
        butterflies: 6,      // Anzahl Schmetterlinge (Desktop, mobil halbiert)
        petals:      6,      // Anzahl Blütenblätter
        fleeRadius:  170,    // ab dieser Distanz zum Zeiger weichen sie aus (px)
        fleeForce:   1.1,    // Heftigkeit des Ausweichens
        maxSpeed:    3.6,    // Fluchttempo (px pro Frame bei 60 fps)
        butterflySpeed: 0.7, // Reisetempo Schmetterling
        petalSpeed:     0.3, // Reisetempo Blütenblatt
        wander:      0.07,   // Richtungswechsel: höher als sonst, Falter taumeln
        calmDown:    0.012,  // wie schnell der Schreck abklingt
        margin:      70,     // ab diesem Randabstand drehen sie ein
        butterflySize: [18, 34],
        petalSize:     [12, 22]
    };

    var WING_COLORS  = ["#e8734a", "#f2b544", "#7fb7d9", "#c76fa8", "#8ec96a", "#e4d15a"];
    var PETAL_COLORS = ["#ffd7e4", "#ffe9d0", "#f3d9ff", "#fff2cc", "#ffd9c4"];

    var BUTTERFLY =
        '<span class="ea-critter__body">' +
        '<i class="ea-wing ea-wing--l"></i>' +
        '<i class="ea-bfly-body"></i>' +
        '<i class="ea-wing ea-wing--r"></i>' +
        '</span>';

    var PETAL = '<span class="ea-critter__body"></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".ea-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isButterfly = kind === "butterfly";
            var range = isButterfly ? CONFIG.butterflySize : CONFIG.petalSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "ea-critter ea-critter--" + kind;
                el.innerHTML = isButterfly ? BUTTERFLY : PETAL;
                el.style.setProperty("--ea-size", size.toFixed(1) + "px");
                el.style.setProperty(
                    isButterfly ? "--ea-wing" : "--ea-petal",
                    pick(isButterfly ? WING_COLORS : PETAL_COLORS)
                );
                el.style.opacity = isButterfly ? "1" : rand(0.6, 0.95).toFixed(2);
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isButterfly ? CONFIG.butterflySpeed : CONFIG.petalSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isButterfly ? size / 1.35 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("butterfly", small ? Math.ceil(CONFIG.butterflies / 2) : CONFIG.butterflies);
        spawn("petal", small ? Math.ceil(CONFIG.petals / 2) : CONFIG.petals);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "butterfly") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--ea-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.eaReady) return;
        scene.dataset.eaReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--ea-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--ea-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".ea-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-easter");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}